import axios from 'axios';
import { supabase } from './supabase';
import { BACKEND_URL } from './config';
import { SUPABASE_URL } from '../auth/constants';
import { getGuestRequestHeaders, persistVisitorFromHeaders } from '../utils/guestSearch';

async function getValidAccessToken() {
    try {
        // Add timeout to prevent hanging
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('getSession timeout after 3s')), 3000)
        );
        
        const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
        return session?.access_token;
    } catch (error) {
        // Try to get from localStorage as fallback
        try {
            const projectRef = SUPABASE_URL.split('//')[1].split('.')[0];
            const storageKey = `sb-${projectRef}-auth-token`;
            const storedSession = localStorage.getItem(storageKey);
            if (storedSession) {
                const parsed = JSON.parse(storedSession);
                if (parsed?.access_token) {
                    return parsed.access_token;
                }
            }
        } catch (fallbackError) {
            // Silent fallback failure
        }
        return null;
    }
}

async function makeAuthenticatedRequest(endpoint, options = {}) {
    try {
        let token = await getValidAccessToken();
        const fullUrl = `${BACKEND_URL}${endpoint}`;
        
        const doRequest = async (bearer) => {
            try {
                const config = {
                    headers: bearer ? { 'Authorization': `Bearer ${bearer}` } : {},
                    ...options,
                    timeout: 10000, // 10 second timeout
                };
                // Support both GET and DELETE methods
                if (options.method === 'DELETE') {
                    return await axios.delete(fullUrl, config);
                }
                return await axios.get(fullUrl, config);
            } catch (requestError) {
                // Check if request was blocked
                if (requestError.code === 'ERR_BLOCKED_BY_CLIENT' || 
                    requestError.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
                    requestError.message?.includes('blocked')) {
                    const blockedError = new Error('Request blocked by browser extension');
                    blockedError.code = 'ERR_BLOCKED_BY_CLIENT';
                    throw blockedError;
                }
                throw requestError;
            }
        };

        let response = await doRequest(token);

        if (response.status === 401 || response.status === 403) {
            const refreshed = await supabase.auth.refreshSession();
            token = refreshed.data?.session?.access_token || token;
            response = await doRequest(token);
        }

        // Check for error responses from backend
        if (response.data && response.data.error) {
            const error = new Error(response.data.error);
            error.status = response.status;
            throw error;
        }

        return response.data;
    } catch (error) {
        throw error;
    }
}

export const fetchUserTopSearches = async (timeRange = '30d', limit = 5) => {
    try {
        // Map frontend time range values to backend format
        // Backend expects: '7d', '30d', '90d', 'all'
        let backendTimeRange = timeRange === 'all_time' ? 'all' : timeRange;
        
        // Validate time range matches backend expectations
        if (!['7d', '30d', '90d', 'all'].includes(backendTimeRange)) {
            console.warn(`Invalid time range: ${timeRange}, defaulting to '30d'`);
            backendTimeRange = '30d';
        }
        
        const params = new URLSearchParams({
            time_range: backendTimeRange,
            limit: limit.toString()
        });
        
        const result = await makeAuthenticatedRequest(`/api/analytics/top-searches?${params}`);
        return result;
    } catch (error) {
        // Handle blocked requests (ad blockers, browser extensions)
        if (error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
            error.code === 'ERR_BLOCKED_BY_CLIENT' ||
            error.message?.includes('blocked') ||
            error.message?.includes('Failed to fetch')) {
            console.warn('Analytics request blocked by browser extension or network issue');
            throw new Error('Analytics request blocked');
        }
        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('TIMEOUT')) {
            console.error('Analytics request timed out:', {
                code: error.code,
                message: error.message,
                url: `${BACKEND_URL}/api/analytics/top-searches`,
                backendUrl: BACKEND_URL
            });
            throw new Error('Analytics request timed out');
        }
        // Handle network errors
        if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
            console.warn('Analytics request failed - network error');
            throw new Error('Analytics network error');
        }
        // Handle HTTP error responses from backend
        if (error.response) {
            const status = error.response.status;
            const errorMessage = error.response.data?.error || error.message || 'Analytics request failed';
            console.warn(`Analytics request failed with status ${status}:`, errorMessage);
            if (status === 400) {
                throw new Error(`Invalid request: ${errorMessage}`);
            } else if (status === 401 || status === 403) {
                throw new Error('Authentication failed - please log in again');
            } else if (status >= 500) {
                throw new Error('Analytics backend error - please try again later');
            }
            throw new Error(errorMessage);
        }
        throw error;
    }
};

export const fetchUserSearchStats = async (timeRange = '30d') => {
    try {
        // Map frontend time range values to backend format
        // Backend expects: '7d', '30d', '90d', 'all'
        let backendTimeRange = timeRange === 'all_time' ? 'all' : timeRange;
        
        // Validate time range matches backend expectations
        if (!['7d', '30d', '90d', 'all'].includes(backendTimeRange)) {
            console.warn(`Invalid time range: ${timeRange}, defaulting to '30d'`);
            backendTimeRange = '30d';
        }
        
        const params = new URLSearchParams({
            time_range: backendTimeRange
        });
        
        return await makeAuthenticatedRequest(`/api/analytics/search-stats?${params}`);
    } catch (error) {
        throw error;
    }
};

export const deleteUserData = async () => {
    try {
        const params = new URLSearchParams();
        return await makeAuthenticatedRequest(`/api/user/delete-data?${params}`, {
            method: 'DELETE'
        });
    } catch (error) {
        // Handle blocked requests (ad blockers, browser extensions)
        if (error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
            error.code === 'ERR_BLOCKED_BY_CLIENT' ||
            error.message?.includes('blocked') ||
            error.message?.includes('Failed to fetch')) {
            console.warn('Delete user data request blocked by browser extension or network issue');
            throw new Error('Request blocked');
        }
        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('TIMEOUT')) {
            console.error('Delete user data request timed out');
            throw new Error('Request timed out');
        }
        // Handle network errors
        if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
            console.warn('Delete user data request failed - network error');
            throw new Error('Network error');
        }
        // Handle HTTP error responses from backend
        if (error.response) {
            const status = error.response.status;
            const errorMessage = error.response.data?.error || error.message || 'Failed to delete user data';
            console.warn(`Delete user data request failed with status ${status}:`, errorMessage);
            if (status === 400) {
                throw new Error(`Invalid request: ${errorMessage}`);
            } else if (status === 401 || status === 403) {
                throw new Error('Authentication failed - please log in again');
            } else if (status >= 500) {
                throw new Error('Backend error - please try again later');
            }
            throw new Error(errorMessage);
        }
        throw error;
    }
};

export const trackSearch = async (query, platform = 'combined', resultCount = 0) => {
    try {
        // Validate input to match backend requirements
        if (!query || typeof query !== 'string' || query.trim().length === 0) {
            console.warn('Invalid search query for tracking:', query);
            return null;
        }
        
        // Trim and limit query length to match backend (max 256 chars)
        const trimmedQuery = query.trim();
        if (trimmedQuery.length > 256) {
            console.warn('Search query too long, truncating:', trimmedQuery);
            query = trimmedQuery.substring(0, 256);
        } else {
            query = trimmedQuery;
        }
        
        // Ensure resultCount is a number
        const numResultCount = parseInt(resultCount) || 0;
        
        const token = await getValidAccessToken();
        const headers = token
            ? { Authorization: `Bearer ${token}` }
            : getGuestRequestHeaders();

        const response = await axios.post(`${BACKEND_URL}/api/searches/track`, {
            search_query: query,
            platform: platform || 'combined',
            result_count: numResultCount,
            search_success: numResultCount > 0
        }, {
            headers,
            timeout: 5000 // 5 second timeout for tracking (non-critical)
        });

        persistVisitorFromHeaders(response.headers);
        return response.data;
    } catch (error) {
        // Silently fail for tracking - don't interrupt user experience
        if (error.response) {
            console.warn('Search tracking failed:', error.response.data?.error || error.message);
        } else {
            console.warn('Search tracking failed:', error.message);
        }
        return null;
    }
};

